import {SerializedStyles, CSSObject, Theme} from "@emotion/react";
import {Font} from "./theme/types";
import {CrossBrand} from "./theme/brands/crossbrand/types";

/**
 * Defines the shape of a react-stitch helper function,
 * @typeParam Props - The props the helper expects to receive, usually maps to the props of the component using this function,
 * @typeParam ReturnType - The return type of the function, typically a serialized style, a style string, or object,
 * */
export type StyleFn<
  Props = Record<string, unknown>,
  ReturnType = SerializedStyles
> = {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-types */
  (props: Props & {theme: Theme}): ReturnType extends object ? any : ReturnType;
};

type BaseProps = {
  theme: Theme;
  crossBrand?: boolean;
};

/**
 * Helper to resolve the current theme,
 * @param props.theme - The current theme,
 * @param props.crossBrand - Whether we should render crossBrand styles,
 * @returns Either the current theme, or current theme's crossBrand theme,
 */
export const getTheme: StyleFn<BaseProps, CrossBrand | Theme> = ({
  theme,
  crossBrand,
}) => {
  if (crossBrand) return theme.crossBrand;
  return theme;
};

/**
 * Helper to access a theme's fonts,
 * @param fontKey - The type of font to return, typically "primary" or "secondary",
 * @returns a StyleFn, when provided with props, will return the correct font,
 */
export const getFont = (
  fontKey: keyof Theme["font"]
): StyleFn<BaseProps, Font | undefined> => ({
  theme,
  crossBrand,
}): Font | undefined => {
  return getTheme({theme, crossBrand}).font[fontKey];
};

export const isProp = <Props>(propName: keyof Props, style: CSSObject) => (
  props: Props
): typeof style | false | undefined | null => props[propName] && style;
